import { useQueryClient } from "@tanstack/react-query";
import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { prefetchAgents } from "@/lib/api/hooks";
import { requestJson } from "@/lib/api/http";

export type Workspace = {
	id: string;
	name: string;
	slug: string;
	role: "owner" | "admin" | "member";
};

type SessionUser = {
	id: string;
	name: string;
	email: string;
};

type AuthSessionState = {
	isLoading: boolean;
	isAuthenticated: boolean;
	user: SessionUser | null;
	workspaces: Workspace[];
	activeWorkspaceId: string;
};

type AuthSessionContextValue = {
	isLoading: boolean;
	isAuthenticated: boolean;
	user: SessionUser | null;
	workspaces: Workspace[];
	activeWorkspace: Workspace | null;
	signIn: (input: { email: string; password: string }) => Promise<void>;
	register: (input: { name: string; email: string; password: string }) => Promise<void>;
	signOut: () => void;
	switchWorkspace: (workspaceId: string) => void;
	createWorkspace: (input: { name: string; slug?: string }) => Promise<void>;
	/** Show a saved rename in the shell without reloading the session. */
	renameWorkspace: (workspaceId: string, name: string) => void;
};

type BetterAuthSession = {
	session: {
		id: string;
		userId: string;
		activeOrganizationId?: string | null;
	};
	user: {
		id: string;
		name: string;
		email: string;
	};
};

type BetterAuthOrganization = {
	id: string;
	name: string;
	slug: string;
};

type BetterAuthActiveRole = {
	role: string;
};

const AuthSessionContext = createContext<AuthSessionContextValue | null>(null);

const loggedOutState: AuthSessionState = {
	isLoading: false,
	isAuthenticated: false,
	user: null,
	workspaces: [],
	activeWorkspaceId: "",
};

/** better-auth stores several roles comma-separated; the strongest one wins, as on the server. */
export function toWorkspaceRole(value: string | undefined): Workspace["role"] {
	const roles = value?.split(",").map((role) => role.trim()) ?? [];
	if (roles.includes("owner")) return "owner";
	if (roles.includes("admin")) return "admin";
	return "member";
}

function slugifyWorkspaceName(name: string): string {
	const normalized = name
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9\s-]/g, "")
		.replace(/\s+/g, "-")
		.replace(/-+/g, "-")
		.replace(/^-|-$/g, "")
		.slice(0, 64);

	if (normalized.length > 0) {
		return normalized;
	}

	return `workspace-${Math.random().toString(36).slice(2, 8)}`;
}

async function resolveSessionState(): Promise<AuthSessionState> {
	const session = await requestJson<BetterAuthSession | null>("/api/auth/get-session", {
		method: "GET",
	});

	if (!session) {
		return loggedOutState;
	}

	const organizations = await requestJson<BetterAuthOrganization[]>("/api/auth/organization/list", {
		method: "GET",
	});

	let activeWorkspaceId =
		session.session.activeOrganizationId ??
		organizations[0]?.id ??
		loggedOutState.activeWorkspaceId;

	if (!session.session.activeOrganizationId && activeWorkspaceId) {
		await requestJson("/api/auth/organization/set-active", {
			method: "POST",
			body: { organizationId: activeWorkspaceId },
		});
	}

	if (!activeWorkspaceId && organizations[0]?.id) {
		activeWorkspaceId = organizations[0].id;
	}

	const activeRole = activeWorkspaceId
		? await requestJson<BetterAuthActiveRole>("/api/auth/organization/get-active-member-role", {
				method: "GET",
			}).catch(() => ({ role: "member" }))
		: { role: "member" };

	const workspaces = organizations.map((organization) => ({
		id: organization.id,
		name: organization.name,
		slug: organization.slug,
		role: organization.id === activeWorkspaceId ? toWorkspaceRole(activeRole.role) : "member",
	}));

	return {
		isLoading: false,
		isAuthenticated: true,
		user: {
			id: session.user.id,
			name: session.user.name,
			email: session.user.email,
		},
		workspaces,
		activeWorkspaceId,
	};
}

export function AuthSessionProvider({ children }: PropsWithChildren) {
	const queryClient = useQueryClient();
	const [state, setState] = useState<AuthSessionState>({
		...loggedOutState,
		isLoading: true,
	});

	const refreshSession = useCallback(async () => {
		setState((current) => ({ ...current, isLoading: true }));
		try {
			const next = await resolveSessionState();
			// Warm the agent registry while the boot screen still covers the
			// console, so the landing page paints with data instead of a
			// second spinner. prefetchQuery swallows errors, so a failure
			// here just leaves the page to its own fetch-and-retry.
			if (next.isAuthenticated && next.activeWorkspaceId) {
				await prefetchAgents(queryClient);
			}
			setState(next);
		} catch {
			setState(loggedOutState);
		}
	}, [queryClient]);

	useEffect(() => {
		void refreshSession();
	}, [refreshSession]);

	const signIn = useCallback(
		async ({ email, password }: { email: string; password: string }) => {
			await requestJson("/api/auth/sign-in/email", {
				method: "POST",
				body: { email, password },
			});
			await refreshSession();
		},
		[refreshSession],
	);

	const register = useCallback(
		async ({ name, email, password }: { name: string; email: string; password: string }) => {
			await requestJson("/api/auth/sign-up/email", {
				method: "POST",
				body: {
					name,
					email,
					password,
					callbackURL: `${window.location.origin}/verify-email`,
				},
			});
		},
		[],
	);

	const signOut = useCallback(() => {
		setState(loggedOutState);
		queryClient.clear();
		void requestJson("/api/auth/sign-out", {
			method: "POST",
		}).catch(() => {
			return;
		});
	}, [queryClient]);

	const switchWorkspace = useCallback(
		(workspaceId: string) => {
			setState((current) => ({
				...current,
				activeWorkspaceId: workspaceId,
			}));

			void requestJson("/api/auth/organization/set-active", {
				method: "POST",
				body: {
					organizationId: workspaceId,
				},
			})
				.then(() => queryClient.invalidateQueries())
				.catch(() => {
					void refreshSession();
				});
		},
		[queryClient, refreshSession],
	);

	const createWorkspace = useCallback(
		async ({ name, slug }: { name: string; slug?: string }) => {
			const normalizedName = name.trim();
			if (!normalizedName) {
				throw new Error("Workspace name is required");
			}

			const normalizedSlug = slug?.trim() || slugifyWorkspaceName(normalizedName);
			const created = await requestJson<{ id: string }>("/api/auth/organization/create", {
				method: "POST",
				body: {
					name: normalizedName,
					slug: normalizedSlug,
				},
			});

			await requestJson("/api/auth/organization/set-active", {
				method: "POST",
				body: {
					organizationId: created.id,
				},
			});

			await refreshSession();
			await queryClient.invalidateQueries();
		},
		[queryClient, refreshSession],
	);

	const renameWorkspace = useCallback((workspaceId: string, name: string) => {
		setState((current) => ({
			...current,
			workspaces: current.workspaces.map((workspace) =>
				workspace.id === workspaceId ? { ...workspace, name } : workspace,
			),
		}));
	}, []);

	const value = useMemo<AuthSessionContextValue>(() => {
		const activeWorkspace =
			state.workspaces.find((workspace) => workspace.id === state.activeWorkspaceId) ?? null;

		return {
			isLoading: state.isLoading,
			isAuthenticated: state.isAuthenticated,
			user: state.user,
			workspaces: state.workspaces,
			activeWorkspace,
			signIn,
			register,
			signOut,
			switchWorkspace,
			createWorkspace,
			renameWorkspace,
		};
	}, [createWorkspace, register, renameWorkspace, signIn, signOut, state, switchWorkspace]);

	return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>;
}

export function useAuthSession() {
	const context = useContext(AuthSessionContext);
	if (!context) {
		throw new Error("useAuthSession must be used inside AuthSessionProvider");
	}
	return context;
}
